import React, { ChangeEventHandler, FormEvent, FormEventHandler } from 'react'
import { Form, FormGroup, Input, Label, FormFeedback } from 'reactstrap'
import Select from './Select'
import { BulkOperationRequest } from '../../api/bulkOperations'
import { validators } from '../../utils'
import Confirmation from './Confirmation'
import ExportCSV from '../generic/ExportCSV'
import { downloadFileFromBlob } from '../../utils/file'

interface BulkOperationMove {
  dataset: File | null
  targetProductionSite: string
  recalculateCustomerPromiseDate: boolean,
  comment: string
}

interface State {
  data: BulkOperationMove
  showCpdConfirmation: boolean
  productionSites: { plant: string; description: string }[]
  disabled: boolean
  errors: {
    dataset: boolean,
    comment: boolean
  }
}

interface BulkMoveFormProps {
  input: BulkOperationRequest
  onSubmit(form: BulkOperationRequest): void
}

export default class BulkMoveForm extends React.Component<BulkMoveFormProps, State> {
  constructor(props: BulkMoveFormProps) {
    super(props)

    this.state = {
      data: {
        recalculateCustomerPromiseDate: false,
        targetProductionSite: '',
        dataset: null,
        comment: '',
      },
      showCpdConfirmation: false,
      productionSites: [
        { plant: 'ELA', description: 'Move to ELA' },
        { plant: 'ELA_SE', description: 'Move to ELA_SE' },
        { plant: 'HAR_UK', description: 'Move to HAR_UK' },
        { plant: 'JONDO', description: 'Move to JONDO' },
        { plant: 'KHS', description: 'Move to KHS' },
        { plant: 'MM', description: 'Move to MM' },
        { plant: 'NLH', description: 'Move to NLH' },
        { plant: 'ORWO', description: 'Move to ORWO' },
        { plant: 'PAT', description: 'Move to PAT' },
        { plant: 'PP', description: 'Move to PP' },
        { plant: 'RAV', description: 'Move to RAV' },
        { plant: 'WFR', description: 'Move to WFR' },
        { plant: 'YPB', description: 'Move to YPB' },
      ],
      disabled: false,
      errors: {
        dataset: false,
        comment: false
      },
    }

    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleTargetProductionSiteChange = this.handleTargetProductionSiteChange.bind(this)
    this.handleRecalculateCustomerPromiseDateChange = this.handleRecalculateCustomerPromiseDateChange.bind(this)
    this.handleUploadFile = this.handleUploadFile.bind(this)
    this.toggleConfirmation = this.toggleConfirmation.bind(this)
    this.onConfirmCpd = this.onConfirmCpd.bind(this)
  }

  private toggleConfirmation() {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        recalculateCustomerPromiseDate: false,
      },
      showCpdConfirmation: false,
    }))
  }

  private onConfirmCpd() {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        recalculateCustomerPromiseDate: true,
      },
      showCpdConfirmation: false,
    }))
  }

  downloadTemplate = () => {
    const data = [
      ['ProductCode', 'ProductionOrderId'],
      ['AL020132281259', '13768a24-2b9f-495a-b295-b2f8346c6443'],
      ['AL020132281260', '13768a24-2b9f-495a-b295-b2f8346c6443'],
      ['AL020132281262', '13768a24-2b9f-495a-b295-b2f8346c6443'],
      ['AL020132281261', '13768a24-2b9f-495a-b295-b2f8346c6443'],
    ]
    const csvString = data.map((row) => row.join(',')).join('\r\n')
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
    const fileName = 'BulkOperationDataTemplate.csv'
    downloadFileFromBlob(blob, fileName)
  }

  renderCpdConfirmation() {
    const { showCpdConfirmation } = this.state
    return (
      <Confirmation
        title="Do you want to change the Customer Promise Date?"
        isOpen={showCpdConfirmation}
        toggle={this.toggleConfirmation}
        onSubmit={this.onConfirmCpd}
        submitText="Yes"
        cancelText="No">
        Are you sure that you want to change original Customer Promise Date? This will recalculate the Customer Promise Date for
        all production orders being moved.
      </Confirmation>
    )
  }

  handleSubmit(event: FormEvent) {
    event.preventDefault()

    let errors = {};

    if (!this.state.data.dataset) {
      errors = { ...errors, dataset: true };
    }

    if (!this.state.data.comment) {
      errors = { ...errors, comment: true };
    }

    if (Object.entries(errors).length) {
      this.setState({
        ...this.state,
        errors: { ...this.state.errors, ...errors },
      } as State)
      return;
    }

    this.setState({
      ...this.state,
    } as State)
    this.setState({ disabled: true })
    let bulkOperationRequest: BulkOperationRequest = {
      dataset: this.state.data.dataset,
      operationType: this.props.input.operationType,
      parameters: {
        targetProductionSite: this.state.data.targetProductionSite,
        recalculateCustomerPromiseDate: this.state.data.recalculateCustomerPromiseDate,
        comment: this.state.data.comment,
      },
    }
    this.props.onSubmit(bulkOperationRequest)
    this.setState({ disabled: false })
  }

  handleTargetProductionSiteChange: ChangeEventHandler<HTMLSelectElement> = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        targetProductionSite: value,
      },
    }))
  }

  handleRecalculateCustomerPromiseDateChange: ChangeEventHandler<HTMLInputElement> = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        recalculateCustomerPromiseDate: checked,
      },
      showCpdConfirmation: checked,
    }))
  }

  handleUploadFile: ChangeEventHandler<HTMLInputElement> = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type } = event.target
    let value: string | boolean | File | null = event.target.value

    if (type === 'checkbox') {
      value = (event.target as HTMLInputElement).checked
    } else if (type === 'file') {
      value = (event.target as HTMLInputElement).files ? (event.target as HTMLInputElement).files![0] : null
    }

    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        dataset: value as File | null,
      },
    }))
  }

  handleCommentChange: ChangeEventHandler<HTMLInputElement> = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        comment: value,
      },
    }))
  }

  render() {
    // --- Style Definitions ---

    const formContainerStyle = {
      maxWidth: 'auto',
      margin: '30px auto',
      padding: '40px',
      border: '1px solid var(--color-border, #ddd)',
      backgroundColor: 'var(--color-bg-white, #fff)',
    }

    const headerStyle = {
      borderBottom: '2px solid var(--color-primary, #555)',
      paddingBottom: '10px',
      marginBottom: '30px',
      color: 'var(--color-text-primary, #333)',
    }

    const fieldHelpTextStyle = {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 600,
      color: 'var(--color-text-secondary, #555)',
    }

    const templateLinkStyle = {
      marginBottom: '15px',
      color: 'var(--color-info, #007ace)',
      fontSize: '0.9em',
    }

    const fileInputContainerStyle = {
      border: '1px dashed var(--color-border-dark, #ccc)',
      padding: '15px',
      backgroundColor: 'var(--color-bg-light, #f8f9fa)',
    }

    const checkboxGroupStyle = {
      marginTop: '25px',
      marginBottom: '25px',
      padding: '10px',
      border: '1px solid var(--color-border, #eee)',
      backgroundColor: 'var(--color-bg-alt, #fafafa)',
    }

    const labelStyle = {
      fontWeight: 500,
      color: 'var(--color-text-primary, #333)',
    }

    const submitButtonStyle = {
      padding: '12px 25px',
      backgroundColor: 'var(--color-submit, #003952)',
      color: 'white',
      border: 'none',
      cursor: this.state.disabled ? 'not-allowed' : 'pointer',
      fontWeight: 600,
      fontSize: '1.1em',
      opacity: this.state.disabled ? 0.6 : 1,
      transition: 'background-color 0.2s, opacity 0.2s',
      width: '100%', // Full width for the main action
    }

    // --- Form Rendering ---

    return (
      <Form onSubmit={this.handleSubmit} encType="multipart/form-data" style={formContainerStyle}>
        <h1 style={headerStyle}>Bulk Move Form</h1>

        {/* File Upload Section */}
        <FormGroup>
          <p style={templateLinkStyle}>
            {/* Note: The 'here' part should be an actual <a> tag */}
            Download template input file{' '}
            <a onClick={this.downloadTemplate} style={{ color: 'var(--color-primary, #007bff)', textDecoration: 'underline', cursor: 'pointer' }}>
              here
            </a>
          </p>

          <div style={fileInputContainerStyle}>
            {/* Note: In React, you style the label and hide the default file input for better UI */}
            <label style={fieldHelpTextStyle}>Upload production orders file</label>
            <Input
              type="file"
              name="dataFile"
              id="datafile"
              onChange={this.handleUploadFile}
            // Custom styling for file input might require additional CSS classes
            />
          </div>
          {/* Error Feedback */}
          {this.state.errors.dataset && (
            <FormFeedback style={{ display: 'block', color: 'var(--color-danger, #dc3545)' }}>
              {validators.required(this.state.data.dataset?.name)}
            </FormFeedback>
          )}
        </FormGroup>

        {/* Checkbox Group */}
        <FormGroup check style={checkboxGroupStyle}>
          <Input
            type="checkbox"
            name="recalculateCustomerPromiseDate"
            id="recalculateCustomerPromiseDateCheckbox"
            checked={this.state.data.recalculateCustomerPromiseDate}
            onChange={this.handleRecalculateCustomerPromiseDateChange}
            style={{ marginRight: '10px' }} // Spacing for the checkbox
          />
          <Label for="recalculateCustomerPromiseDateCheckbox" style={labelStyle}>
            Recalculate Customer Promise Date
          </Label>
        </FormGroup>

        {/* Target Production Site Select */}
        <FormGroup>
          <Label className="col-form-label" style={labelStyle}>
            Target Production Site
          </Label>
          {/* Assuming 'Select' is a custom component that handles internal styling */}
          <Select
            emptyText="-- select a target production site --"
            onChange={this.handleTargetProductionSiteChange}
            value={this.state.data.targetProductionSite}
            options={this.state.productionSites.map((x) => ({ value: x.plant, text: x.description }))}
            invalid={false}
          />
        </FormGroup>
        <FormGroup>
          <Label className="col-form-label">Comment</Label>
          <Input type="textarea" rows={5} name="comment" id="bulkMoveComment" value={this.state.data.comment} onChange={this.handleCommentChange} invalid={this.state.errors.comment} />
          {this.state.errors.comment && (
            <FormFeedback style={{ display: 'block', color: 'var(--color-danger, #dc3545)' }}>
              {validators.required(this.state.data.comment)}
            </FormFeedback>
          )}
        </FormGroup>

        {/* Submit Button */}
        <FormGroup>
          <button type="submit" id="bulkMoveSubmit" disabled={this.state.disabled} style={submitButtonStyle}>
            Submit
          </button>
        </FormGroup>

        {this.renderCpdConfirmation()}
      </Form>
    )
  }
}
